<template>
  <div class="flex flex-col w-full">
    <h1 class="text-xl font-bold text-gray-900 px-1">
      {{ $t("admin.project_settings.tab_project_participants") }}
    </h1>
    <div class="pt-6" v-show="invalidInput">
      <div class="rounded-md bg-red-50 p-4">
        <div class="flex">
          <div class="shrink-0">
            <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">
              {{ this.errorMessageInvalidInput }}
            </h3>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col py-6 w-full">
      <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block 3xl:min-w-2/3 2xl:w-2/3 py-2 align-middle sm:px-6 lg:px-8">
          <div class="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
            <div class="divide-y divide-gray-200 sm:block">
              <div class="bg-gray-50 sm:grid-cols-6 sm:gap-2 items-center hidden sm:grid">
                <div
                  class="sm:px-6 px-3 py-3 col-span-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {{ $t("admin.project_participants.table_user") }}
                </div>
                <div
                  class="sm:px-6 px-3 py-3 col-span-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {{ $t("admin.project_participants.table_permissions") }}
                </div>
                <div
                  class="sm:px-6 px-3 py-3 col-span-1 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {{ $t("admin.project_participants.table_delete") }}
                </div>
              </div>
              <div class="divide-y divide-gray-200 bg-white">
                <div
                  v-for="participant in projectParticipants"
                  :key="participant._id"
                  class="grid sm:grid-cols-6 items-center sm:gap-4 gap-2 py-4"
                >
                  <div class="col-span-2 sm:px-6 px-3 text-sm break-all font-medium text-gray-900">
                    {{ participant.email }}
                  </div>
                  <div
                    class="flex flex-wrap col-span-3 items-center whitespace-nowrap gap-1 sm:px-6 px-3 text-sm text-gray-500"
                  >
                    <div
                      v-for="permission in participant.feature_permission"
                      :key="permission"
                      v-show="showPermissionChips"
                    >
                      <div v-if="permission.displayed_name === this.permissionsProjectAdmin">
                        <span
                          class="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                      <div v-if="permission.displayed_name === this.permissionsCameraFeatures">
                        <span
                          class="inline-flex rounded-full bg-gray-300 px-2 text-xs font-semibold leading-5 text-gray-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                      <div v-if="permission.displayed_name === this.permissionsLabourHeatmap">
                        <span
                          class="inline-flex rounded-full bg-yellow-300 px-2 text-xs font-semibold leading-5 text-yellow-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                      <div v-if="permission.displayed_name === this.permissionsPlanner">
                        <span
                          class="inline-flex rounded-full bg-yellow-300 px-2 text-xs font-semibold leading-5 text-yellow-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                      <div v-if="permission.displayed_name === this.permissionsProcesses">
                        <span
                          class="inline-flex rounded-full bg-yellow-300 px-2 text-xs font-semibold leading-5 text-yellow-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                      <div v-if="permission.displayed_name === this.permissionsDailyReport">
                        <span
                          class="inline-flex rounded-full bg-yellow-300 px-2 text-xs font-semibold leading-5 text-yellow-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                      <div v-if="permission.displayed_name === this.permissionsContractor">
                        <span
                          class="inline-flex rounded-full bg-orange-200 px-2 text-xs font-semibold leading-5 text-yellow-800"
                        >
                          {{ permission.displayed_name }}
                        </span>
                      </div>
                    </div>
                    <div class="pl-2">
                      <a href="#">
                        <PencilIcon
                          @click.prevent="
                            editFeaturePermissionModal = true;
                            setFeaturePermissionsModal(participant, participant.feature_permission);
                          "
                          id="feature-add-button"
                          class="mr-3 h-4 text-oaiGray-300 hover:text-yellow-600"
                          aria-hidden="true"
                        />
                      </a>
                    </div>
                  </div>
                  <div class="col-span-1 sm:px-0 px-3 text-sm break-words text-gray-500">
                    <a
                      href="#"
                      @click.prevent="
                        deleteUserModal = true;
                        sendDeleteUserInfo(participant.username);
                      "
                      class="block sm:px-4 text-sm text-red-500"
                      >{{ $t("admin.project_participants.table_remove_user") }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Add user -->
    <button
      class="self-start focus:outline-none inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
      v-on:click="
        choiceAddUserModal = true;
        invalidInput = false;
      "
    >
      <PlusIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
      {{ $t("admin.project_participants.add_participants") }}
    </button>
  </div>

  <!-- Add User Choice Modal -->
  <TransitionRoot class="z-50" as="template" :show="choiceAddUserModal">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="choiceAddUserModal = false">
      <div
        class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div>
              <div class="space-y-8 divide-y divide-gray-200 pb-4 sm:space-y-5">
                <div>
                  <h3 class="text-lg font-medium leading-6 text-gray-900">
                    {{ $t("admin.project_participants.modal_choice_add_user.add_user") }}
                  </h3>
                </div>
              </div>
              <div class="pt-4 sm:border-t sm:border-gray-200">
                <div class="">
                  <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:col-start-1 sm:mt-0 sm:text-sm"
                    v-on:click="
                      choiceAddUserModal = false;
                      addExistingUserModal = true;
                    "
                  >
                    <PlusIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.modal_choice_add_user.add_existing_user") }}
                  </button>
                </div>
                <div class="pt-4">
                  <button
                    type="submit"
                    class="inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 sm:col-start-2 sm:text-sm"
                    v-on:click="
                      choiceAddUserModal = false;
                      inviteNewUserModal = true;
                    "
                  >
                    <EnvelopeIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.modal_choice_add_user.add_registration") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Delete User Modal-->
  <TransitionRoot class="z-50" as="template" :show="deleteUserModal">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="deleteUserModal = false">
      <div
        class="flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div>
              <div class="space-y-8 divide-y divide-gray-200 sm:pb-4 pb-2 sm:space-y-5">
                <div>
                  <h3 class="sm:text-lg text-base font-medium leading-6 text-gray-900">
                    {{ $t("admin.project_participants.modal_delete_user.delete_user") }}
                  </h3>
                </div>
              </div>
              <div class="sm:pt-4 pt-2 sm:border-t sm:border-gray-200">
                <div class="">
                  <button
                    type="button"
                    class="sm:mt-3 inline-flex items-center w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-xs sm:text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:col-start-1 sm:mt-0 sm:text-sm"
                    v-on:click="this.deleteExistingUser(this.selectedUser, 'project')"
                  >
                    <MinusIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.modal_delete_user.delete_from_project") }}
                  </button>
                </div>
                <div class="pt-4" v-if="hasPermission('app_admin')">
                  <button
                    type="submit"
                    class="inline-flex items-center w-full justify-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-xs sm:text-base font-medium text-white shadow-sm hover:bg-red-600 sm:col-start-2 sm:text-sm"
                    v-on:click="this.deleteExistingUser(this.selectedUser, 'permanently')"
                  >
                    <XMarkIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.modal_delete_user.delete_permanently") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Add User Modal -->
  <TransitionRoot class="z-50" as="template" :show="addExistingUserModal">
    <Dialog
      as="div"
      class="fixed inset-0 z-10 overflow-y-auto"
      @close="addExistingUserModal = false"
    >
      <div
        class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div>
              <form
                @submit.prevent="addExistingUser"
                class="space-y-8 divide-y divide-gray-200"
                action="#"
                method="POST"
              >
                <div class="space-y-8 divide-y divide-gray-200 sm:space-y-5">
                  <div>
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                      {{ $t("admin.project_participants.modal_add_existing_user.add_user") }}
                    </h3>
                  </div>
                  <div
                    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5"
                  >
                    <label
                      for="user-mail"
                      class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      {{ $t("admin.project_participants.modal_add_existing_user.email") }}
                    </label>
                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                      <input
                        type="text"
                        name="user-mail"
                        id="user-mail"
                        v-model="userMail"
                        class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-green-300 focus:ring-green-300 sm:max-w-xs sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div
                  class="mt-5 pt-4 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3"
                >
                  <button
                    type="submit"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                    @click="addExistingUserModal = false"
                  >
                    <PlusIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.modal_add_existing_user.add") }}
                  </button>
                  <button
                    type="button"
                    class="focus:outline-none mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                    @click="addExistingUserModal = false"
                    ref="cancelButtonRef"
                  >
                    {{ $t("admin.project_participants.modal_add_existing_user.cancel") }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Edit Feature Permission Modal -->
  <TransitionRoot class="z-50" as="template" :show="editFeaturePermissionModal">
    <Dialog
      as="div"
      class="fixed inset-0 z-10 overflow-y-auto"
      @close="
        editFeaturePermissionModal = false;
        this.selectedFeatures = {};
      "
    >
      <div
        class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div>
              <form
                @submit.prevent="updateFeaturePermissions"
                class="space-y-8 divide-y divide-gray-200"
                action="#"
                method="POST"
              >
                <div>
                  <h3 class="pt-2 text-lg font-medium leading-6 text-gray-900">
                    {{ this.selectedUserObject.email }}
                  </h3>
                </div>
                <fieldset class="space-y-5 pt-4">
                  <div v-for="feature in projectFeatures" :key="feature.feature">
                    <div v-if="feature.feature === 'live_camera_feature_user'" class="pb-8">
                      <hr />
                    </div>
                    <div class="relative flex items-start">
                      <div class="flex h-5 items-center">
                        <input
                          @click="adjustFeaturePermissionModal(feature.feature)"
                          :id="feature.feature"
                          :aria-describedby="feature.feature + '-description'"
                          :name="feature.feature"
                          v-model="selectedFeatures[feature.feature]"
                          type="checkbox"
                          class="h-4 w-4 rounded border-gray-300 text-green-400 focus:ring-white-500"
                        />
                      </div>
                      <div class="ml-3 text-sm">
                        <label :for="feature.feature" class="font-small text-gray-700"
                          >{{ feature.displayed_name }}
                        </label>
                      </div>
                    </div>
                  </div>
                </fieldset>
                <div
                  class="mt-5 pt-4 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3"
                >
                  <button
                    type="submit"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                    @click="editFeaturePermissionModal = false"
                  >
                    <PlusIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.modal_permission.update") }}
                  </button>
                  <button
                    type="button"
                    class="focus:outline-none mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                    @click="
                      editFeaturePermissionModal = false;
                      this.selectedFeatures = {};
                    "
                    ref="cancelButtonRef"
                  >
                    {{ $t("admin.project_participants.modal_permission.cancel") }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Invite New User Modal -->
  <TransitionRoot class="z-50" as="template" :show="inviteNewUserModal">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="inviteNewUserModal = false">
      <div
        class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div>
              <form
                @submit.prevent="inviteNewUser"
                class="space-y-8 divide-y divide-gray-200"
                action="#"
                method="POST"
              >
                <div class="space-y-8 divide-y divide-gray-200 sm:space-y-5">
                  <div>
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                      {{ $t("admin.project_participants.invite_new_user.invite") }}
                    </h3>
                  </div>
                  <div
                    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5"
                  >
                    <label
                      for="user-mail"
                      class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      {{ $t("admin.project_participants.invite_new_user.email") }}
                    </label>
                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                      <input
                        type="text"
                        name="user-mail"
                        id="user-mail"
                        v-model="userMail"
                        class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-green-300 focus:ring-green-300 sm:max-w-xs sm:text-sm"
                      />
                    </div>
                  </div>
                  <div
                    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5"
                  >
                    <label
                      for="first-name"
                      class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      {{ $t("admin.project_participants.invite_new_user.first_name") }}
                    </label>
                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                      <input
                        type="text"
                        name="first-name"
                        id="first-name"
                        v-model="firstName"
                        class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-green-300 focus:ring-green-300 sm:max-w-xs sm:text-sm"
                      />
                    </div>
                  </div>
                  <div
                    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5"
                  >
                    <label
                      for="last-name"
                      class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      {{ $t("admin.project_participants.invite_new_user.last_name") }}
                    </label>
                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                      <input
                        type="text"
                        name="last-name"
                        id="last-name"
                        v-model="lastName"
                        class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-green-300 focus:ring-green-300 sm:max-w-xs sm:text-sm"
                      />
                    </div>
                  </div>
                  <div
                    class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5"
                  >
                    <label
                      for="company-name"
                      class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      {{ $t("admin.project_participants.invite_new_user.company") }}
                    </label>
                    <div class="mt-1 sm:col-span-2 sm:mt-0">
                      <input
                        type="text"
                        name="company-name"
                        id="company-name"
                        v-model="companyName"
                        class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-green-300 focus:ring-green-300 sm:max-w-xs sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div
                  class="mt-5 pt-4 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3"
                >
                  <button
                    type="submit"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                    @click="inviteNewUserModal = false"
                  >
                    <PlusIcon class="-ml-1 mr-3 h-5 w-5" aria-hidden="true" />
                    {{ $t("admin.project_participants.invite_new_user.add") }}
                  </button>
                  <button
                    type="button"
                    class="focus:outline-none mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                    @click="inviteNewUserModal = false"
                    ref="cancelButtonRef"
                  >
                    {{ $t("admin.project_participants.invite_new_user.cancel") }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from "@headlessui/vue";
import {
  PlusIcon,
  EnvelopeIcon,
  XCircleIcon,
  MinusIcon,
  XMarkIcon,
  PencilIcon,
} from "@heroicons/vue/24/solid";
import ProjectRepository from "@/repositories/ProjectRepository";
import UserRepository from "@/repositories/UserRepository";
import logger from "@/services/logger";

export default {
  name: "OldProjectParticipants",
  data() {
    return {
      projectParticipants: [],
      projectFeatures: [],
      selectedFeatures: {},
      choiceAddUserModal: false,
      addExistingUserModal: false,
      inviteNewUserModal: false,
      deleteUserModal: false,
      editFeaturePermissionModal: false,
      invalidInput: false,
      errorMessageInvalidInput: "",
      selectedUser: "",
      selectedUserObject: {},
      showPermissionChips: true,
      permissionsProjectAdmin: this.$t("admin.project_participants.permission_features.admin"),
      permissionsCameraFeatures: this.$t(
        "admin.project_participants.permission_features.camera_features",
      ),
      permissionsContractor: this.$t("admin.project_participants.permission_features.contractor"),
      permissionsPlanner: this.$t("app_features.planner"),
      permissionsProcesses: this.$t("app_features.processes"),
      permissionsLabourHeatmap: this.$t("app_features.labour_heatmap"),
      permissionsDailyReport: this.$t("app_features.daily_report"),
      userMail: "",
      firstName: "",
      lastName: "",
      companyName: "",
    };
  },
  mounted() {
    this.getProjectParticipants();
    this.getProjectFeatures();
  },
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    PlusIcon,
    EnvelopeIcon,
    XCircleIcon,
    MinusIcon,
    XMarkIcon,
    PencilIcon,
  },
  methods: {
    loadUsersForProject() {
      if (this.hasPermission("app_admin")) {
        return UserRepository.loadAllUsersForProject(this.currentCustomerName, this.currentSiteId);
      }

      return UserRepository.loadUsersForProject(this.currentCustomerName, this.currentSiteId);
    },
    getProjectParticipants() {
      this.showPermissionChips = true;

      this.loadUsersForProject()
        .then((users) => {
          for (let i = 0; i < users.length; i++) {
            let participant = users[i];

            participant = this.mapDisplayedName(participant);
            this.projectParticipants.push(participant);
          }
        })
        .catch((error) => {
          logger.error(error);
        });
    },
    getProjectFeatures() {
      this.projectFeatures.push({
        feature: "project_admin",
        displayed_name: this.permissionsProjectAdmin,
      });

      ProjectRepository.loadProject(this.currentCustomerName, this.currentSiteId)
        .then((project) => {
          if (project.features.includes("camera")) {
            this.projectFeatures.push({
              feature: "all_camera_features_user",
              displayed_name: this.permissionsCameraFeatures,
            });
          }

          if (project.features.includes("planner")) {
            this.projectFeatures.push({
              feature: "planner_user",
              displayed_name: this.permissionsPlanner,
            });
          }
          if (project.features.includes("processes")) {
            this.projectFeatures.push({
              feature: "processes_user",
              displayed_name: this.permissionsProcesses,
            });
          }
          if (project.features.includes("heatmap")) {
            this.projectFeatures.push({
              feature: "heatmap_user",
              displayed_name: this.permissionsLabourHeatmap,
            });
          }
          if (project.features.includes("daily_report")) {
            this.projectFeatures.push({
              feature: "daily_report_user",
              displayed_name: this.permissionsDailyReport,
            });
          }

          if (project.features.includes("camera")) {
            this.projectFeatures.push({
              feature: "live_camera_feature_user",
              displayed_name: this.permissionsContractor,
            });
          }
        })
        .catch((error) => {
          logger.error(error);
        });
    },

    async addExistingUser() {
      this.invalidInput = false;
      // Remove whitespaces, special characters and convert to lower-case
      const userMailCleaned = this.userMail.replace(/\s/g, "").toLowerCase();

      if (this.projectParticipants.some((user) => user.email === userMailCleaned)) {
        this.invalidInput = true;
        this.errorMessageInvalidInput = this.$t(
          "admin.project_participants.error_messages.add_user_already_exists",
        );

        return;
      }

      const project_features = {};

      // By default assign all project features apart form admin and contractor
      for (let i = 0; i < this.projectFeatures.length; i++) {
        if (
          this.projectFeatures[i]["feature"] !== "project_admin" &&
          this.projectFeatures[i]["feature"] !== "live_camera_feature_user"
        ) {
          project_features[this.projectFeatures[i]["feature"]] = true;
        }
      }

      const groups = Object.entries(project_features)
        .filter(([, value]) => value)
        .map(([key]) => key);

      UserRepository.setUserProjectPermissionByEmail(
        userMailCleaned,
        this.currentCustomerName,
        this.currentSiteId,
        groups,
      )
        .then(({ userFound }) => {
          if (userFound) {
            this.projectParticipants = [];
            this.getProjectParticipants();
          } else {
            this.invalidInput = true;
            this.errorMessageInvalidInput = this.$t(
              "admin.project_participants.error_messages.add_not_registered",
            );
          }
        })
        .catch((error) => {
          logger.error(error);
        });
    },

    deleteExistingUser(user, type) {
      const userObj = this.projectParticipants.find((participant) => participant.username === user);

      this.invalidInput = false;

      if (type === "project") {
        UserRepository.setUserProjectPermissionByEmail(
          userObj.email,
          this.currentCustomerName,
          this.currentSiteId,
          [],
        )
          .then(() => {
            this.projectParticipants = [];
            this.getProjectParticipants();
          })
          .catch((error) => {
            logger.error(error);
          });
      } else {
        UserRepository.deleteUser(user)
          .then(() => {
            this.projectParticipants = [];
            this.getProjectParticipants();
          })
          .catch((error) => {
            logger.error(error);
          });
      }
      this.deleteUserModal = false;
    },

    inviteNewUser() {
      const formFields = [this.userMail, this.firstName, this.lastName, this.companyName];

      for (let i = 0; i < formFields.length; i++) {
        if (formFields[i].length === 0) {
          this.invalidInput = true;
          this.errorMessageInvalidInput = this.$t(
            "admin.project_participants.invite_new_user.error_fill_all_fields",
          );

          return;
        }
      }

      // Remove whitespaces, special characters and convert to lower-case
      const userMailCleaned = this.userMail.replace(/\s/g, "").toLowerCase();
      const firstNameCleaned = this.firstName.trim().replace(/[&/\\#,+()$~%.'":*?<>{}]/g, "");
      const lastNameCleaned = this.lastName.trim().replace(/[&/\\#,+()$~%.'":*?<>{}]/g, "");
      const companyNameCleaned = this.companyName.trim().replace(/[&/\\#,+()$~%.'":*?<>{}]/g, "");

      UserRepository.inviteUser(
        this.currentCustomerName,
        this.currentSiteId,
        userMailCleaned,
        firstNameCleaned,
        lastNameCleaned,
        companyNameCleaned,
      )
        .then(() => {
          this.addExistingUser();
        })
        .catch((error) => {
          if (error.response.data.detail.includes("User already exists.")) {
            this.errorMessageInvalidInput = this.$t(
              "admin.project_participants.error_messages.register_user_already_exists",
            );
          } else {
            this.errorMessageInvalidInput = this.$t(
              "admin.project_participants.error_messages.register_contact_oculai",
            );
            logger.error(error);
          }
          this.invalidInput = true;
        });
    },
    setFeaturePermissionsModal(user, featurePermissions) {
      this.selectedUserObject = user;

      for (let i = 0; i < featurePermissions.length; i++) {
        this.selectedFeatures[featurePermissions[i]["feature"]] = true;
      }
    },
    updateFeaturePermissions() {
      const groups = Object.entries(this.selectedFeatures)
        .filter(([, value]) => value)
        .map(([key]) => key);
      UserRepository.setUserProjectPermissionByEmail(
        this.selectedUserObject.email,
        this.currentCustomerName,
        this.currentSiteId,
        groups,
      )
        .then(() => {
          this.projectParticipants = [];
          this.getProjectParticipants();
        })
        .catch((error) => {
          logger.error(error);
        });

      this.selectedFeatures = {}; // Empty selected features object
    },
    sendDeleteUserInfo(item) {
      this.selectedUser = item;
    },
    mapDisplayedName(user) {
      const features = [];

      if (this.hasPermissionForUser(user, "project_admin")) {
        features.push({
          feature: "project_admin",
          displayed_name: this.permissionsProjectAdmin,
          status: "active",
          position: 1,
        });
      }
      if (this.hasPermissionForUser(user, "all_camera_features_user")) {
        features.push({
          feature: "all_camera_features_user",
          displayed_name: this.permissionsCameraFeatures,
          status: "active",
          position: 2,
        });
      }
      if (this.hasPermissionForUser(user, "planner_user")) {
        features.push({
          feature: "planner_user",
          displayed_name: this.permissionsPlanner,
          status: "active",
          position: 3,
        });
      }
      if (this.hasPermissionForUser(user, "processes_user")) {
        features.push({
          feature: "processes_user",
          displayed_name: this.permissionsProcesses,
          status: "active",
          position: 4,
        });
      }
      if (this.hasPermissionForUser(user, "heatmap_user")) {
        features.push({
          feature: "heatmap_user",
          displayed_name: this.permissionsLabourHeatmap,
          status: "active",
          position: 5,
        });
      }
      if (this.hasPermissionForUser(user, "live_camera_feature_user")) {
        features.push({
          feature: "live_camera_feature_user",
          displayed_name: this.permissionsContractor,
          status: "active",
          position: 7,
        });
      }
      if (this.hasPermissionForUser(user, "daily_report_user")) {
        features.push({
          feature: "daily_report_user",
          displayed_name: this.permissionsDailyReport,
          status: "active",
          position: 8,
        });
      }

      features.sort((a, b) => a.position - b.position);
      user["feature_permission"] = features;

      return user;
    },
    adjustFeaturePermissionModal(feature) {
      setTimeout(() => {
        if (feature === "live_camera_feature_user") {
          this.selectedFeatures = { live_camera_feature_user: true };
        } else {
          this.selectedFeatures["live_camera_feature_user"] = Object.values(
            this.selectedFeatures,
          ).every((value) => !value);
        }
      }, 0);
    },
  },
};
</script>

<style scoped></style>
